import { useMemo, useState } from "react";
import { ADModalBody, ADModalInfo, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { ManagedWindow, WindowStateActions } from "src/WindowManager/WindowState";
import { GlassInputGroup, GlassInputGroupTitle, GlassTextInputTransformer } from "./GlassInput";
import { GlassFoldUdBox, ViewButtonText } from "src/propertyInfoTemplates/PropertyComponents";
import { parseGetCapabilities, XMLResponseToUTF8 } from "src/dataCollector/WFSCollector";
import mitSoft from '../mit-soft-settings.json' with { type: "json" };
import mitSoftVector from '../mit-soft-vector.json' with { type: "json" };
import { Spinner } from "react-bootstrap";
import { GlassInfo } from "./MitGlassComponents";

export function LayerTester(props: { window: ManagedWindow }) {

   let { dispatch: windowDispath } = useWindowContext();
   let [layerFContent, setLayerFContent] = useState<(MITLayer & { valid?: string })[] | null>(null);
   let [fileError, setFileError] = useState<string[]>([]);
   let [loading, setLoading] = useState<boolean>(false);


   function validateFileContent(content: string | null): boolean {
      console.log("Invalid layer file format", content);
      let json = JSON.parse(content || "{}");
      if ("standardBackgroundLayers" in json) {
         setLayerFContent(json["standardBackgroundLayers"]);
         setFileError([]);
         return true;
      }
      if ("customBackgroundLayers" in json) {
         setLayerFContent(json["customBackgroundLayers"]);
         setFileError([]);
         return true;
      }
      if ("dynamicBackgroundLayers" in json) {
         setLayerFContent(json["dynamicBackgroundLayers"]);
         setFileError([]);
         return true;
      }
      if ("vectorBackgroundLayers" in json) {
         setLayerFContent(json["vectorBackgroundLayers"]);
         setFileError([]);
         return true;
      }
      setFileError((pre) => [...pre, "Invalid layer file format."]);
      return false;
   }

   let getCapabilitiesURL = (layer: MITLayer): string => {
      let url = "";
      if (layer.type === undefined) {
         url = `${layer.host}${layer.path}?service=WMS&request=GetCapabilities&version=${layer.version || "1.3.0"}` + (layer.urlParams ? Object.entries(layer.urlParams).map(([key, value]) => `&${key}=${value}`).join('') : "");
      } else if (layer.type === "geojsonWFS") {
         let urlObj = new URL(layer.src);
         let keys = [...urlObj.searchParams.keys()]
         let notDelete = ["VERSION", "version", "username", "password"];
         keys.filter((a) => !notDelete.includes(a)).forEach((key) => {
            urlObj.searchParams.delete(key);
         });
         urlObj.searchParams.append("service", "WFS");
         urlObj.searchParams.append("request", "GetCapabilities");
         url = urlObj.toString();
      }
      if (url) {
         return "https://" + url.split("://").at(-1);
      }
      return "";
   };

   async function testLayers() {
      setLoading(true);
      let layers = layerFContent as MITLayer[];
      let capabilitiesURL = new Set<string>();
      let capabilities: {
         url: string;
         layers: string[];
      }[] = []
      layers.map((layer) => {
         let url = getCapabilitiesURL(layer)
         if (url) {
            capabilitiesURL.add(url);
         }
      });
      console.log("Testing layers with capabilities URLs:", capabilitiesURL);
      await Promise.allSettled([...capabilitiesURL].map((url) => {
         return new Promise((resolve) => {
            fetch(url).then((response) => {
               return response
            }).then(async (result) => {
               if (!result) {
                  return capabilities.push({ url: url, layers: [] });
               }
               let xmlText = await XMLResponseToUTF8(result);
               if (xmlText) {
                  let layers = parseGetCapabilities(xmlText || "", true);
                  capabilities.push({ url: url, layers: layers.map(l => l?.toLowerCase() || "") });
               }
               resolve(null);
            }).catch(() => {
               capabilities.push({ url, layers: [] });
               resolve(null);
            });
         });
      }));
      setLayerFContent(layers.map((layer) => {
         let result
         let url = getCapabilitiesURL(layer);
         let layerARR = layer.type ? layer.src?.match?.(/TYPENAMES=([^&]+)/)?.[1] : layer.layers;
         let allLayersID = layerARR ? layerARR.split(",") : [];
         let capability = capabilities.find(c => c.url === url);
         let capLayers = capability?.layers || [];
         if (capability) {
            if (allLayersID.every(id => capLayers.includes(id.toLowerCase()) || capLayers.includes(id.toLowerCase().split(":").at(-1) || ""))) {
               result = "success";
            } else {
               console.log("Layer test warning for layer:", layer.label, "Expected layers:", allLayersID, "Capabilities layers:", capLayers);
               result = "error";
            }
         } else {
            result = "confused";
         }
         return {
            ...layer,
            valid: result
         }
      }));
      setLoading(false);
   }

   let groupedLayerTest = useMemo(() => {
      return Object.groupBy(layerFContent || [], (layer) => layer.valid || "untested");
   }, [layerFContent]);


   return (
      <AdvancedDragModal window={props.window}
         title={"Layer Tester"}
         onClose={() => { windowDispath({ type: WindowStateActions.Close, windowId: props.window.id }) }}
         PosDefault={{ left: "50%", top: "5%", transform: "translate(-50%, 0%)" }}
         subHeaderContent={<>
            {layerFContent ? <ViewButtonText onClick={() => { setLayerFContent(null); }} >Close file</ViewButtonText> : <></>}
            {layerFContent ? (loading ? <Spinner animation="border" size="sm" /> : <ViewButtonText onClick={() => { testLayers(); }} >Test Layers</ViewButtonText>) : <> </>}
         </>}
      >
         <ADModalBody>

            {fileError.length > 0 && (
               <ADModalInfo>
                  {fileError.map((error, index) => (
                     <div style={{ color: " red" }} key={index}>{error}</div>
                  ))}
               </ADModalInfo>
            )}
            {layerFContent == null ?
               <GlassInputGroup>
                  <GlassInputGroupTitle>Upload or Select layer file</GlassInputGroupTitle>
                  <GlassTextInputTransformer label={"Upload layer file"}>
                     <input
                        type="file"
                        accept=".json"
                        onChange={(e) => {
                           if (e.target.files && e.target.files.length > 1) {
                              setFileError((pre) => [...pre, "Please select only one file."]);
                              return;
                           }
                           const file = e.target.files?.[0];
                           if (file) {
                              const reader = new FileReader();
                              reader.onload = (event) => {
                                 const content = event.target?.result as string;
                                 validateFileContent(content);
                              };
                              reader.readAsText(file);
                           }
                        }
                        }
                        placeholder="Enter layer file content here"
                     />
                  </GlassTextInputTransformer>
                  <GlassTextInputTransformer label={"Select MIT Soft layers"}>
                     <button onClick={() => {
                        setLayerFContent(mitSoft.standardBackgroundLayers as MITLayer[]);
                     }} >Standard</button>
                     <button onClick={() => {
                        setLayerFContent(mitSoftVector.vectorBackgroundLayers as MITLayer[]);
                     }} >Vector</button>
                  </GlassTextInputTransformer>

               </GlassInputGroup>
               : <></>}
            {
               Object.keys(groupedLayerTest ?? {}).map((key) => (
                  <>
                     <GlassFoldUdBox key={key} title={`Result: ${key} (${groupedLayerTest[key]?.length || 0} layers)` + resultIcons(key)} foldetUd={false} >
                        <GlassInfo>{resultExplanation(key)}</GlassInfo>
                        {groupedLayerTest[key]?.map((layer, index) => (
                           <div key={index} style={{ display: "flex", justifyContent: "space-between", padding: "0.2em 0" }}>
                              <div>{resultIcons(layer.valid)}</div>
                              <div style={{ flex: "1 1" }} >{layer.label}</div>
                              <button className="LayerTestBtn" onClick={() => open(getCapabilitiesURL(layer))} >🌍</button>
                           </div>
                        ))}
                     </GlassFoldUdBox>
                  </>
               ))
            }
         </ADModalBody>
      </AdvancedDragModal>
   )
}

function resultIcons(result: any): string {
   const icons = {
      "success": "✅",
      "error": "❌",
      "confused": "🤔",
      "diff": "🔄",
      "warning": "⚠️"
   };
   return icons[result] || "⁉️";
}

function resultExplanation(result: any): string {
   const explanations = {
      "success": "Alle lagets lag fundet i capabilities.",
      "error": "Nogle eller alle lag ikke fundet i capabilities. (Disse kan stadig virke?).",
      "confused": "Kunne ikke finde capabilities, måske grundet anden type service (eg. ES/Martin).",
      "diff": "Test result ikke implementeret endnu.",
      "warning": "Test result ikke implementeret endnu."
   };
   return explanations[result] || "Result explanation not available.";
}

type MITLayer = WMSLayer | WFSLayer;

type WMSLayer = {
   label: string;
   type: undefined;
   layers: string;
   version: string;
   host: string;
   path: string;
   urlParams: { [key: string]: string };
}

type WFSLayer = {
   label: string;
   type: "geojsonWFS";
   src: string;
}